<template>
  <div class="message-outer-container">
    <ChatTimestamp v-if="showTimestamp" class="timestamp" :date="date" />
    <div
      v-if="!message.deletedAt"
      :class="[
        'message-container',
        {
          'is-reply': replyMessage,
          'is-by-sender': isBySender,
          'no-bottom-padding': showTimestamp,
          'no-padding': !showTimestamp,
        },
      ]"
    >
      <div v-if="showSenderName" class="sender-name">
        <FriedH5>{{ senderFullName }}</FriedH5>
        <FriedLabel class="message-timestamp">
          {{ formattedTimestamp }}
        </FriedLabel>
      </div>
      <div v-if="replyMessage" class="reply-container">
        <div class="reply-content" :data-external="`name:${senderFullName}-reply`">
          <div class="reply-string">
            <FriedIcon icon="email-action-reply" :size="IconSize.Small" />
            <FriedH5>{{ replyStringPrefix }}</FriedH5>
            <FriedLabel>{{ replyStringRepliedTo }}</FriedLabel>
            <FriedH5>{{ replyStringName }}</FriedH5>
            <FriedLabel>{{ formattedTimestamp }}</FriedLabel>
          </div>
          <FriedParagraph
            :size="ParagraphSize.Small"
            data-testid="reply-text"
            :data-external="`name:${senderFullName}-reply-message-content:${replyMessageText}`"
            >{{ replyMessageText }}</FriedParagraph
          >
        </div>
      </div>
      <ChatMessageContent
        :participants="participants"
        :message="message"
        :messages="messages"
        :current-chat-participant="currentChatParticipant"
        :locale="locale"
        :can-edit="canEdit"
        :last-reads="lastReads"
        :current-chat-user="currentChatUser"
        @delete="$emit('delete', $event)"
        @reply="$emit('reply', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { watch, computed, defineComponent, ref, nextTick } from 'vue';
import { ParagraphSize, IconSize } from '@getaccept/fried-tofu';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import type { ChatParticipant, ChatParticipantId, LastReadBy, Message, ReadBy } from '../types';
import ChatHelper from '../helpers/chat.helper';
import type { Participant } from '../../types';
import AvatarHelper from '../../helpers/avatar.helper';
import ChatTimestamp from './ChatTimestamp.vue';
import ChatMessageContent from './ChatMessageContent.vue';

export default defineComponent({
  components: {
    ChatTimestamp,
    ChatMessageContent,
  },
  props: {
    participants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    message: {
      type: Object as PropType<Message>,
      default: undefined,
    },
    messages: {
      type: Array as PropType<Array<Message>>,
      default: () => [],
    },
    currentChatParticipant: {
      type: Object as PropType<ChatParticipant>,
      default: undefined as ChatParticipant,
    },
    lastReads: {
      type: Array as PropType<Array<LastReadBy>>,
      default: () => [],
    },
    locale: {
      type: String,
      default: '',
    },
    tabVisible: {
      type: Boolean,
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    currentChatUser: {
      type: Object as PropType<ChatParticipantId>,
      default: () =>
        ({
          participantId: '',
          userId: '',
        }) as ChatParticipantId,
    },
  },
  emits: ['read', 'delete', 'reply'],
  setup(props, { emit }) {
    const { t } = useI18n();

    const messageState = computed(() =>
      ChatHelper.getMessageState(
        props.message,
        props.participants,
        props.messages,
        props.currentChatParticipant,
        props.locale
      )
    );

    const replyMessage = computed(() => messageState.value.replyMessage);

    const { name, thumbUrl, date, fullTimestamp, isBySender } = messageState.value;

    const previousMessage = computed(() =>
      ChatHelper.getPreviousMessage(props.message, props.messages)
    );

    const showSenderName = computed(() => {
      if (isBySender) {
        return false;
      }
      if (!previousMessage.value) {
        return true;
      }
      return props.message.ownerId !== previousMessage.value.ownerId;
    });

    const showTimestamp = computed(() =>
      ChatHelper.showTimestamp(props.messages, previousMessage.value, props.message.createdAt)
    );

    const sender = computed(() => {
      const { owner, ownerId } = props.message;

      if (!owner.participantId && !owner.userId && !ownerId) {
        return;
      }

      return props.participants.find(
        ({ id, newParticipantId }) =>
          id === owner.participantId ||
          id === owner.userId ||
          (ownerId && ownerId === newParticipantId)
      );
    });

    const formattedTimestamp = computed(() =>
      DateTime.fromMillis(props.message.createdAt)
        .setLocale(props.locale)
        .toLocaleString(DateTime.TIME_SIMPLE)
    );

    const senderFullName = computed(() =>
      sender.value ? AvatarHelper.getDisplayName(sender.value) : ''
    );

    const messageRead = ref(false);

    watch(
      () => props.tabVisible,
      async () => {
        await nextTick();

        if (props.tabVisible && !messageRead.value) {
          const hasRead = props.message.reads.find(({ participant, participantId }: ReadBy) =>
            ChatHelper.isSameParticipant(props.currentChatParticipant, participant, participantId)
          );
          if (!hasRead && !props.message.isDraft) {
            emit('read', props.message.id);
            messageRead.value = true;
          }
        }
      },
      { immediate: true }
    );

    const isSameSender = (participant: Participant) =>
      ChatHelper.isSameParticipant(
        props.currentChatParticipant,
        replyMessage.value?.owner,
        replyMessage.value?.ownerId
      ) && participant;

    const messageOwner = computed(() => {
      const { participantId, userId } = replyMessage.value?.owner || {};

      return (
        (participantId || userId || replyMessage.value?.ownerId) &&
        props.participants.find(
          ({ id }) => id === participantId || id === userId || id === replyMessage.value?.ownerId
        )
      );
    });

    const replyStringPrefix = computed(() => (isBySender ? `${t('you')} ` : ''));

    const replyStringRepliedTo = computed(() =>
      replyStringPrefix.value ? t('dr_chat_replied_to').toLowerCase() : t('dr_chat_replied_to')
    );

    const getReplyName = (participant: Participant, replyParticipant: ChatParticipant) => {
      if (participant) {
        return AvatarHelper.getDisplayName(participant);
      }
      if (replyParticipant?.userId) {
        return t('dr_chat_previous_owner');
      }
      return t('dr_chat_removed_participant');
    };

    const replyStringName = computed(() =>
      isSameSender(messageOwner.value)
        ? t('you').toLowerCase()
        : getReplyName(messageOwner.value, replyMessage.value.owner)
    );

    const replyMessageText = computed(() =>
      replyMessage.value.deletedAt
        ? `${t('deleted')} ${ChatHelper.fullTimestamp(replyMessage.value.deletedAt).toLocaleLowerCase()}`
        : replyMessage.value.text
    );

    return {
      name,
      thumbUrl,
      date,
      fullTimestamp,
      isBySender,
      showTimestamp,
      replyMessage,
      IconSize,
      sender,
      ParagraphSize,
      t,

      previousMessage,
      showSenderName,
      messageRead,
      getReplyName,
      senderFullName,
      formattedTimestamp,
      replyStringPrefix,
      replyStringRepliedTo,
      replyStringName,
      replyMessageText,
    };
  },
});
</script>

<style lang="scss" scoped>
.message-outer-container {
  display: flex;
  flex-direction: column;
  width: 100%;

  .message-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: var(--spacing-50) 0;
    box-sizing: border-box;
    color: var(--text-gray);

    .sender-name {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      margin-left: calc(var(--spacing-200) + var(--spacing-200) + var(--spacing-75));
      margin-top: var(--spacing-150);
      margin-bottom: var(--spacing-50);
      color: var(--text-gray);

      .message-timestamp {
        margin-left: var(--spacing-25);
      }
    }

    .reply-container {
      padding-left: calc(var(--spacing-150) + var(--spacing-200) + var(--spacing-75));
      display: grid;
      grid-template-columns: auto 2rem;
      column-gap: var(--spacing-75);

      .reply-content {
        display: flex;
        flex-direction: column;
        max-width: 480px;
        gap: var(--spacing-50);
        background: var(--gray-98);
        padding: var(--spacing-50) var(--spacing-100);
        border-radius: var(--radius-medium);
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;

        .reply-string {
          display: flex;
          flex-direction: row;
          gap: var(--spacing-25);
        }

        h5,
        p {
          color: var(--text-gray);
        }
      }
    }

    &.is-by-sender {
      align-self: flex-end;

      .reply-container {
        align-self: flex-end;
        padding-top: var(--spacing-100);
        padding-right: var(--spacing-150);
      }
    }

    &.no-bottom-padding {
      padding-bottom: 0;
    }

    &.no-padding {
      padding: 0;
    }

    .is-reply {
      background-color: var(--gray-98);
    }

    .timestamp {
      margin: var(--spacing-100) 0 var(--spacing-150);
    }
  }
}
</style>
